import { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Copy, Check, Key } from 'lucide-react';

interface WireGuardPeerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PeerFormData) => Promise<{ presharedKey?: string; assignedIp?: string } | void>;
  peer?: PeerFormData & { id?: string };
  serverNetwork?: string;
}

interface PeerFormData {
  name: string;
  description?: string;
  publicKey: string;
  assignedIp: string;
  allowedIps: string[];
  persistentKeepalive: number;
  enabled: boolean;
  endpoint?: string;
}

const WireGuardPeerModal = ({
  isOpen,
  onClose,
  onSave,
  peer,
  serverNetwork,
}: WireGuardPeerModalProps) => {
  const [formData, setFormData] = useState<PeerFormData>({
    name: '',
    description: '',
    publicKey: '',
    assignedIp: '',
    allowedIps: ['10.0.0.0/8', '192.168.0.0/16'],
    persistentKeepalive: 25,
    enabled: true,
    endpoint: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedPsk, setGeneratedPsk] = useState<string | null>(null);
  const [copiedPsk, setCopiedPsk] = useState(false);
  const [allowedIpsText, setAllowedIpsText] = useState('10.0.0.0/8, 192.168.0.0/16');

  const isEditing = !!peer?.id;

  useEffect(() => {
    if (peer) {
      setFormData({
        name: peer.name || '',
        description: peer.description || '',
        publicKey: peer.publicKey || '',
        assignedIp: peer.assignedIp || '',
        allowedIps: peer.allowedIps || ['10.0.0.0/8', '192.168.0.0/16'],
        persistentKeepalive: peer.persistentKeepalive ?? 25,
        enabled: peer.enabled ?? true,
        endpoint: peer.endpoint || '',
      });
      setAllowedIpsText(peer.allowedIps?.join(', ') || '10.0.0.0/8, 192.168.0.0/16');
    } else {
      // Reset for new peer
      setFormData({
        name: '',
        description: '',
        publicKey: '',
        assignedIp: '',
        allowedIps: ['10.0.0.0/8', '192.168.0.0/16'],
        persistentKeepalive: 25,
        enabled: true,
        endpoint: '',
      });
      setAllowedIpsText('10.0.0.0/8, 192.168.0.0/16');
    }
    setGeneratedPsk(null);
    setError(null);
  }, [peer, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Peer name is required');
      return;
    }

    if (!formData.publicKey.trim()) {
      setError('Public key is required. Generate one on your IoT gateway first.');
      return;
    }

    if (formData.publicKey.length !== 44) {
      setError('Public key must be 44 characters (base64 encoded)');
      return;
    }

    // Parse allowed IPs
    const allowedIps = allowedIpsText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s);

    if (allowedIps.length === 0) {
      setError('At least one allowed IP network is required (split tunnel)');
      return;
    }

    setSaving(true);
    try {
      const result = await onSave({
        ...formData,
        allowedIps,
      });

      // If creating a new peer, show the PSK
      if (result?.presharedKey) {
        setGeneratedPsk(result.presharedKey);
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save peer');
    } finally {
      setSaving(false);
    }
  };

  const copyPsk = async () => {
    if (generatedPsk) {
      await navigator.clipboard.writeText(generatedPsk);
      setCopiedPsk(true);
      setTimeout(() => setCopiedPsk(false), 2000);
    }
  };

  const handleCloseAfterPsk = () => {
    setGeneratedPsk(null);
    onClose();
  };

  if (!isOpen) return null;

  // PSK Display Modal (after successful creation)
  if (generatedPsk) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Key className="w-6 h-6 text-amber-500" />
            <h3 className="text-lg font-semibold text-gray-900">Save Your Pre-Shared Key</h3>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800 mb-3">
              <strong>Important:</strong> This pre-shared key will only be shown once. Copy it now
              and enter it in your IoT gateway's WireGuard configuration.
            </p>

            <div className="flex items-center space-x-2">
              <code className="flex-1 text-sm font-mono bg-white px-3 py-2 rounded border border-amber-300 break-all">
                {generatedPsk}
              </code>
              <button
                onClick={copyPsk}
                className="btn-secondary px-3 py-2 inline-flex items-center space-x-1"
                title="Copy PSK"
              >
                {copiedPsk ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            If you lose this key, you can regenerate it from the peer settings, but you'll need to
            update your IoT gateway configuration.
          </p>

          <div className="flex justify-end">
            <button onClick={handleCloseAfterPsk} className="btn-primary px-4 py-2">
              I've Saved the Key
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit IoT Gateway Peer' : 'Add IoT Gateway Peer'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gateway Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="input-base w-full"
                placeholder="e.g., Office Opal Gateway"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="input-base w-full"
                placeholder="Optional description"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gateway Public Key <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.publicKey}
              onChange={(e) => setFormData((prev) => ({ ...prev, publicKey: e.target.value }))}
              className="input-base w-full font-mono text-sm"
              placeholder="e.g., dGVzdC1wdWJsaWMta2V5LWJhc2U2NC1lbmNvZGVkLi4u"
              disabled={isEditing} // Can't change public key after creation
            />
            <p className="text-xs text-gray-500 mt-1">
              {isEditing ? (
                'Public key cannot be changed. Create a new peer if needed.'
              ) : (
                <>
                  Generate on your GL.iNet Opal: <strong>VPN → WireGuard Client → Add</strong>, then
                  copy the public key
                </>
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned IP</label>
              <input
                type="text"
                value={formData.assignedIp}
                onChange={(e) => setFormData((prev) => ({ ...prev, assignedIp: e.target.value }))}
                className="input-base w-full font-mono"
                placeholder="Auto-assigned from pool"
                disabled={isEditing}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to auto-assign from {serverNetwork || 'tunnel network'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gateway Endpoint
              </label>
              <input
                type="text"
                value={formData.endpoint}
                className="input-base w-full font-mono bg-gray-50 text-gray-500"
                placeholder="Auto-learned when gateway connects"
                disabled
              />
              <p className="text-xs text-gray-500 mt-1">
                Automatically learned when the gateway connects to the Edge
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Persistent Keepalive (seconds)
              </label>
              <input
                type="number"
                value={formData.persistentKeepalive}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    persistentKeepalive: parseInt(e.target.value) || 0,
                  }))
                }
                min={0}
                max={65535}
                className="input-base w-full"
                placeholder="25"
              />
              <p className="text-xs text-gray-500 mt-1">25 seconds recommended for NAT traversal</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Allowed Networks (Split Tunnel) <span className="text-red-500">*</span>
            </label>
            <textarea
              value={allowedIpsText}
              onChange={(e) => setAllowedIpsText(e.target.value)}
              className="input-base w-full font-mono text-sm"
              rows={3}
              placeholder="10.0.0.0/8, 192.168.0.0/16, 172.16.0.0/12"
            />
            <p className="text-xs text-gray-500 mt-1">
              Comma-separated CIDRs. Only traffic to these networks will use the tunnel.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData((prev) => ({ ...prev, enabled: e.target.checked }))}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
              Enable this peer
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} disabled={saving} className="btn-secondary px-4 py-2">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary inline-flex items-center space-x-2 px-4 py-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{isEditing ? 'Save Changes' : 'Add Peer'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WireGuardPeerModal;
